import { formSchemas } from "../constants/authoring"
import { capitalize } from "lodash"
// helper function to get jpaths
const getJpaths = (obj, lang) => {
  let keys = []

  const hasAdditionalKeys = (obj) => {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        if (key !== "ui:options" && key !== "ui:order" && key !== "ui:widget") {
          return true
        }
      }
    }
    return false
  }

  const concatKeysRecursive = (obj, prefix = "") => {
    let newKey = prefix

    for (let key in obj) {
      if (obj.hasOwnProperty(key)) {
        if (
          key === "ui:widget" ||
          key === "ui:options" ||
          key === "ui:order" ||
          (key === "ui:options" && obj?.["ui:options"]?.hide === true)
        ) {
          continue // Skip keys
        }

        newKey = prefix ? `${prefix}.${key}` : key
        if (hasAdditionalKeys(obj[key]) === false && obj?.[key]?.["ui:options"]?.hide !== true) {
          // if no more nested field, add to results
          keys.push({
            jpath: newKey,
            enLabel: obj[key]?.["ui:options"].title,
            [lang + "Label"]: obj[key]?.["ui:options"]?.translations?.[lang]?.title,
          })
        } else if (typeof obj[key] === "object" && obj[key] !== null && !Array.isArray(obj[key])) {
          // if more nested fields, recurse
          concatKeysRecursive(obj[key], newKey)
        }
      }
    }
  }

  concatKeysRecursive(obj)
  return keys
}

// let jrct = jrctProtocolUISchema()
// let keys = getJpaths(jrct, "jp")

/**
 * @param {Object} formSection format: {id: "results", text: "Results" }
 * @param {String} registryId eg, "ctg", "eudra"
 * @returns string
 */
const getSchemaRegistryTitle = (sectionId, registryId) => {
  return formSchemas[sectionId]?.properties?.[registryId]?.title
}

/**
 * @param {String} sectionId eg, "results", "protocol"
 * @returns string
 */
const getSchemaSectionTitle = (sectionId) => {
  return formSchemas[sectionId]?.title
}

const getFormSections = () => {
  return Object.keys(formSchemas).map((sectId) => {
    return { id: sectId, text: getSchemaSectionTitle(sectId) }
  })
}

const getJpathKey = (currentKey, key) => {
  let newKey = ""
  if (currentKey.length > 0) {
    newKey = `${currentKey}__${key}`
  } else {
    newKey = `${key}`
  }

  return newKey
}

const getDataSchemaFields = (schema, map, currentKey) => {
  if ((schema.type === "string" || (schema.type === "array" && schema.uniqueItems === true)) && !schema[currentKey]) {
    map[currentKey] = schema
    return map
  }

  if (schema.type === "object") {
    if (schema.hasOwnProperty("properties")) {
      Object.keys(schema.properties).forEach((key) => {
        const newKey = getJpathKey(currentKey, key)
        getDataSchemaFields(schema.properties[key], map, newKey)
      })
    }

    if (schema.hasOwnProperty("dependencies")) {
      Object.keys(schema.dependencies).forEach((depKey) => {
        const depArray = schema.dependencies[depKey].oneOf
        depArray.forEach((item) => {
          getDataSchemaFields({ type: "object", ...item }, map, currentKey)
        })
      })
    }
  }

  return map
}

const commonSchemaKeys = new Set([
  "baseline",
  "forecast",
  "actual",
  "phone",
  "first",
  "first name",
  "surname",
  "last",
  "last name",
  "given name",
  "email",
  "zip code",
  "age",
  "post",
  "postal code",
  "central contacts middle name",
  "central contacts first name",
  "central contacts last name",
  "central contacts backup first name",
  "central contacts backup middle name",
  "central contacts backup last name",
  "central contacts backup email",
  "central contacts email",
  "central contacts phone number",
  "central contacts backup phone number",
  "address",
  "central contacts ext",
  "central contacts backup ext",
  "extension",
  "minimum age type",
  "maximum age type",
  "date",
  "planned",
  "actual",
  "link text",
  "url",
  "url text",
  "prefecture",
  "therapeutic area",
  "therapeutic",
  "erad number",
  "countries",
  "post prior to approval clearance",
  "title",
  "study details detailed description",
  "central contacts title",
  "subject information sheet years",
  "protocol summary scientific advice years",
  "impd sande sections investigator brochure years",
  "responses rfi years",
  "address 1",
  "address 2",
  "address 3",
  "address 4",
  "age",
  "minimum age value",
  "maximum age value",
  "age_range_ctis",
  "central_contacts_email",
  "central_contacts_backup_email",
  "affiliation",
  "subject information sheet months",
  "protocol summary scientific advice months",
  "impd sande sections investigator brochure months",
  "responses rfi months",
  "clinical trial results summary intermediate data analysis months",
  "clinical trial results summary lay person summary months",
  "fax",
  "organisation name",
  "city",
  "country",
  "department",
])

const capitalizeLabelSet = new Set([
  "fcsr",
  "fpi",
  "fsa",
  "lpi",
  "lpo",
  "lpop",
  "pca",
  "pra",
  "tlgs",
  "gov",
  "db",
  "lsfd",
  "lplv",
  "fda",
  "ema",
  "cta",
  "eea",
  "smaa",
  "mma",
  "eu",
  "erad",
  "pcd",
  "csr",
  "url",
])

const getUiSchemaFields = (schema, map) => {
  const capitalizeWords = (value) => {
    const words = value.split(" ")
    const formattedWords = words.map((word, i) => {
      if (capitalizeLabelSet.has(word)) {
        return word.toUpperCase()
      } else if (i === 0) {
        return capitalize(word)
      }

      return word
    })

    return formattedWords.join(" ")
  }

  Object.keys(map).forEach((mapKey) => {
    const mapKeys = mapKey.split("__")

    const loopSchemaValue = (schema, mapKeys) => {
      let currSchema = schema
      let value = null
      mapKeys.forEach((key) => {
        if (currSchema.hasOwnProperty(key)) {
          currSchema = currSchema[key]
          value = currSchema
        }
      })

      return value
    }

    const value = loopSchemaValue(schema, mapKeys, 0)

    if (!map[mapKey] || !map[mapKey].label) {
      let label = value?.["ui:options"]?.title
      let fieldType = "string"

      if (mapKeys.length > 2) {
        let lastKey = mapKeys[mapKeys.length - 1].split("_").join(" ")
        let prevKey = mapKeys[mapKeys.length - 2].split("_").join(" ")
        let prevPrevKey = (mapKeys[mapKeys.length - 3] || "")?.split("_")?.join(" ")

        if (lastKey === "fax") {
          console.log({ label, lastKey, prevKey, prevPrevKey })
        }
        if (commonSchemaKeys.has(lastKey)) {
          label = `${label || capitalizeWords(lastKey)} (${capitalizeWords(prevKey)})`
        } else if (lastKey === "enrollment type obs") {
          label = `${label} (Observational)`
        }
      }

      map[mapKey] = {
        label,
        field: mapKey,
        jpath: mapKey,
        field_type: fieldType,
        type: "json_field",
        order: 0,
      }
    }
  })
}

export { getSchemaRegistryTitle, getFormSections, getSchemaSectionTitle, getDataSchemaFields, getUiSchemaFields }
